import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { filterNavItems, type NavItem } from '../lib/filterNavItems';
import { diaADia, HOY } from '../lib/diaADia';
import { normalizarTexto } from '../lib/texto';

interface Bloque {
  /** Subtítulo dentro del grupo (Compras, Stock, Publicaciones en Día a día). */
  label?: string;
  items: NavItem[];
}

interface Grupo {
  label: string;
  bloques: Bloque[];
  plegable: boolean;
  cerradoPorDefecto?: boolean;
}

const DIA_A_DIA = 'Día a día';

// Primero lo de todos los días, en el orden en que se trabaja; después el resto. Cada pantalla aparece una sola vez.
const grupos: Grupo[] = [
  { label: DIA_A_DIA, plegable: false, bloques: [{ items: [HOY] }, ...diaADia] },
  {
    label: 'Principal',
    plegable: true,
    bloques: [{ items: [
      { path: '/dashboard', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1' },
      { path: '/informes', label: 'Informes', icon: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', permisosRequeridos: ['facturas:ver', 'pagos:ver'] },
      { path: '/pendientes', label: 'Pendientes', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', permisosRequeridos: ['facturas:ver', 'pagos:ver'] },
      { path: '/flujo-caja', label: 'Flujo de caja', icon: 'M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z', permisosRequeridos: ['pagos:ver'] },
      { path: '/planner', label: 'Planner', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', permisosRequeridos: ['planner:ver'] },
    ] }],
  },
  {
    label: 'Ventas',
    plegable: true,
    bloques: [{ items: [
      { path: '/clientes', label: 'Clientes', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z', permisosRequeridos: ['clientes:ver'] },
      { path: '/ventas', label: 'Ordenes de Venta', icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z', permisosRequeridos: ['ventas:ver'] },
      { path: '/reservas-despachos', label: 'Reservas y despachos', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4', permisosRequeridos: ['ventas:ver'] },
      { path: '/facturas-venta', label: 'Facturas de Venta', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', permisosRequeridos: ['facturas-venta:ver'] },
      { path: '/cobros', label: 'Cobros', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z', permisosRequeridos: ['cobros:ver'] },
    ] }],
  },
  {
    label: 'Configuración',
    plegable: true,
    cerradoPorDefecto: true,
    bloques: [{ items: [
      { path: '/bom', label: 'BOM', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', permisosRequeridos: ['bom:ver', 'catalogo:ver'] },
      { path: '/insumos', label: 'Insumos', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', permisosRequeridos: ['catalogo:ver'] },
      { path: '/marcas', label: 'Marcas', icon: 'M7 7h-1v2h1V7zm4 0h1v2h-1V7zm4 0h1v2h-1V7zM6 11h12v1H6v-1zm0 3h12v1H6v-1zm0 3h12v1H6v-1zm1-11a1 1 0 011-1h10a1 1 0 011 1v13a1 1 0 01-1 1H7a1 1 0 01-1-1V6z', permisosRequeridos: ['marcas:ver'] },
      { path: '/variantes', label: 'Variantes', icon: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01', permisosRequeridos: ['catalogo:ver'] },
      { path: '/configuracion-mercado-libre', label: 'Mercado Libre y catálogo', icon: 'M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z', permisosRequeridos: ['catalogo:crear', 'catalogo:eliminar'] },
      { path: '/unidades', label: 'U. de Medida', icon: 'M3 6h18M3 12h18M3 18h18', permisosRequeridos: ['unidades:ver'] },
      { path: '/condiciones-pago', label: 'Cond. Pago', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z', permisosRequeridos: ['condiciones-pago:ver'] },
      { path: '/configuracion-impositiva', label: 'Config. Impositiva', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', permisosRequeridos: ['impositivo:ver'] },
      { path: '/retenciones', label: 'Retenciones', icon: 'M9 17v-2a4 4 0 014-4h3m0 0l-3-3m3 3l-3 3M4 7h16M4 12h6m-6 5h9', permisosRequeridos: ['retenciones:ver'] },
      { path: '/iva', label: 'Posición IVA', icon: 'M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z', permisosRequeridos: ['impositivo:ver'] },
      { path: '/actualizacion-precios', label: 'Precios ML', icon: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z', permisosRequeridos: ['catalogo:ver'] },
      { path: '/usuarios', label: 'Usuarios', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', permisosRequeridos: ['usuarios:ver'] },
      { path: '/permisos', label: 'Permisos', icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z', permisosRequeridos: ['permisos:ver'] },
      { path: '/mantenimiento', label: 'Mantenimiento', icon: 'M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437', permisosRequeridos: ['remitos:ver', 'wms:ver'] },
    ] }],
  },
  {
    label: 'Ayuda',
    plegable: true,
    bloques: [{ items: [
      { path: '/instructivos', label: 'Instructivos', icon: 'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25' },
    ] }],
  },
];

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const CLAVE_FIJO = 'sidebar.fijo';
const CLAVE_GRUPOS = 'sidebar.grupos';

/**
 * Preferencias del menú en este navegador. Si no hay almacenamiento (navegación privada, bloqueado) o lo guardado
 * no tiene la forma esperada, se usa el valor por defecto.
 */
function leer<T>(clave: string, esValido: (valor: unknown) => valor is T, porDefecto: T): T {
  try {
    const valor = localStorage.getItem(clave);
    if (valor === null) return porDefecto;
    const parseado: unknown = JSON.parse(valor);
    return esValido(parseado) ? parseado : porDefecto;
  } catch {
    return porDefecto;
  }
}

const esBooleano = (valor: unknown): valor is boolean => typeof valor === 'boolean';

/** Un objeto plano (no null ni array) cuyos valores son todos booleanos: { Ventas: true, Configuración: false }. */
const esGruposAbiertos = (valor: unknown): valor is Record<string, boolean> =>
  typeof valor === 'object' && valor !== null && !Array.isArray(valor) && Object.values(valor).every(esBooleano);

function guardar(clave: string, valor: unknown) {
  try {
    localStorage.setItem(clave, JSON.stringify(valor));
  } catch {
    // Sin almacenamiento: la preferencia dura hasta recargar.
  }
}

const todasLasRutas = grupos.flatMap((g) => g.bloques.flatMap((b) => b.items.map((i) => i.path)));

/** La pantalla del menú que corresponde a la ruta: /compras/123 es Órdenes de compra; gana la ruta más larga. */
function rutaActiva(pathname: string): string | undefined {
  return todasLasRutas
    .filter((p) => pathname === p || pathname.startsWith(`${p}/`))
    .sort((a, b) => b.length - a.length)[0];
}

export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const { user, logout, permisos } = useAuth();
  const [query, setQuery] = useState('');
  const [fijo, setFijo] = useState<boolean>(() => leer(CLAVE_FIJO, esBooleano, true));
  const [abiertos, setAbiertos] = useState<Record<string, boolean>>(() => leer(CLAVE_GRUPOS, esGruposAbiertos, {}));
  const q = normalizarTexto(query);
  const activa = rutaActiva(location.pathname);

  const coincide = (...textos: string[]) => !q || textos.some((t) => normalizarTexto(t).includes(q));

  const visibles = grupos
    .map((grupo) => ({
      ...grupo,
      bloques: grupo.bloques
        .map((bloque) => ({
          ...bloque,
          items: filterNavItems(bloque.items, permisos).filter((item) =>
            coincide(item.label, bloque.label ?? '', grupo.label),
          ),
        }))
        .filter((bloque) => bloque.items.length > 0),
    }))
    .filter((grupo) => grupo.bloques.length > 0);

  const abiertoGuardado = (grupo: Grupo) => abiertos[grupo.label] ?? !grupo.cerradoPorDefecto;
  // Mientras se busca se muestran todos los resultados, aunque el grupo esté cerrado.
  const estaAbierto = (grupo: Grupo) => !grupo.plegable || !!q || abiertoGuardado(grupo);

  const alternarGrupo = (grupo: Grupo) => {
    const siguiente = { ...abiertos, [grupo.label]: !abiertoGuardado(grupo) };
    setAbiertos(siguiente);
    guardar(CLAVE_GRUPOS, siguiente);
  };

  const alternarFijo = () => {
    if (fijo) setQuery(''); // la barra de íconos no filtra
    setFijo(!fijo);
    guardar(CLAVE_FIJO, !fijo);
  };

  const renderItem = (item: NavItem, expanded: boolean, onNavigate?: () => void) => {
    const active = item.path === activa;
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={onNavigate}
        title={!expanded ? item.label : undefined}
        aria-label={!expanded ? item.label : undefined}
        aria-current={active ? 'page' : undefined}
        className={`flex items-center gap-3 py-2 text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-brand-500 ${
          expanded ? 'px-4' : 'justify-center px-2'
        } ${
          active
            ? 'border-r-2 border-brand-500 font-medium'
            : 'border-r-2 border-transparent'
        }`}
        style={
          active
            ? { backgroundColor: '#353A40', color: '#E8823A' }
            : { color: '#C8CED4' }
        }
        onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLElement).style.backgroundColor = '#2A2D31'; }}
        onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLElement).style.backgroundColor = ''; }}
      >
        <svg
          className="w-[18px] h-[18px] shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={active ? 2 : 1.75}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
        </svg>
        {expanded && <span className="truncate">{item.label}</span>}
      </Link>
    );
  };

  const renderNav = (expanded: boolean, onNavigate?: () => void): ReactNode => (
    <nav className="flex-1 overflow-y-auto py-2 sidebar-scrollbar">
      {expanded && visibles.length === 0 && (
        <p className="px-4 py-2 text-sm" style={{ color: '#8A939C' }}>Sin resultados para "{query}"</p>
      )}
      {visibles.map((grupo, i) => {
        // En la barra de íconos no hay grupos que abrir: se ven todas las pantallas.
        const abierto = !expanded || estaAbierto(grupo);
        return (
          <div key={grupo.label} role="group" aria-label={grupo.label} className="mb-1">
            {expanded && grupo.plegable && (
              <button
                type="button"
                onClick={() => alternarGrupo(grupo)}
                aria-expanded={abierto}
                className="w-full flex items-center justify-between px-4 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest transition-colors hover:text-slate-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-brand-500"
                style={{ color: '#8A939C' }}
              >
                {grupo.label}
                <svg
                  className={`w-3 h-3 transition-transform duration-150 ${abierto ? 'rotate-90' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
            {expanded && !grupo.plegable && (
              <p className="px-4 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A939C' }}>
                {grupo.label}
              </p>
            )}
            {!expanded && i > 0 && <div className="mx-3 my-2" style={{ borderTop: '1px solid #363B41' }} />}
            {abierto && grupo.bloques.map((bloque) => (
              <div key={bloque.label ?? grupo.label}>
                {expanded && bloque.label && (
                  <p className="px-4 pt-2 pb-0.5 text-xs font-semibold" style={{ color: '#AEB6BE' }}>{bloque.label}</p>
                )}
                {bloque.items.map((item) => renderItem(item, expanded, onNavigate))}
              </div>
            ))}
          </div>
        );
      })}
    </nav>
  );

  const renderSearch = () => (
    <div className="px-3 pt-3 pb-1">
      <div className="relative">
        <svg className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: '#8A939C' }} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar pantalla..."
          aria-label="Buscar en el menú"
          className="w-full pl-8 pr-3 py-1.5 text-xs rounded focus:outline-none focus:ring-1 focus:ring-brand-500 placeholder:text-[#8A939C]"
          style={{
            backgroundColor: '#2A2D31',
            border: '1px solid #363B41',
            color: '#E8D0B8',
          }}
        />
      </div>
    </div>
  );

  const LogoArea = ({ expanded }: { expanded: boolean }) => {
    const size = expanded ? 40 : 32;
    return (
      <div className="flex items-center gap-2.5 min-w-0">
        <div
          className="shrink-0 overflow-hidden"
          style={{ width: size, height: size, borderRadius: '50%', border: '2px solid #D4631A', backgroundColor: '#fff' }}
        >
          <img
            src="/erp/dobby.png"
            alt="StockeArte"
            style={{ width: size, height: size, objectFit: 'cover', display: 'block' }}
          />
        </div>
        {expanded && (
          <div className="min-w-0">
            <h1 className="text-sm font-bold truncate" style={{ color: '#F0DCC4' }}>StockeArte</h1>
            <p className="text-[10px]" style={{ color: '#8A939C' }}>ERP</p>
          </div>
        )}
      </div>
    );
  };

  const BotonFijar = () => {
    const etiqueta = fijo ? 'Achicar el menú a íconos' : 'Mostrar el menú con nombres';
    return (
      <button
        type="button"
        onClick={alternarFijo}
        title={etiqueta}
        aria-label={etiqueta}
        className="p-1.5 rounded transition-colors hover:bg-[#2A2D31] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-500"
        style={{ color: '#8A939C' }}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d={fijo ? 'M11 19l-7-7 7-7m8 14l-7-7 7-7' : 'M13 5l7 7-7 7M5 5l7 7-7 7'} />
        </svg>
      </button>
    );
  };

  const sidebarStyle = { backgroundColor: '#1E2023', borderRight: '1px solid #363B41' };

  return (
    <>
      {/* Mobile scrim */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`md:hidden fixed inset-y-0 left-0 z-50 w-64 max-w-[85vw] flex flex-col transition-transform duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={sidebarStyle}
        aria-hidden={!mobileOpen}
        {...{ inert: !mobileOpen ? '' : undefined }}
      >
        <div className="py-4 px-4 flex items-center justify-between" style={{ borderBottom: '1px solid #363B41' }}>
          <LogoArea expanded />
          <button
            onClick={onMobileClose}
            title="Cerrar menú"
            aria-label="Cerrar menú"
            className="p-2 -mr-1 rounded transition-colors"
            style={{ color: '#8A939C' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#C8CED4')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#8A939C')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {renderSearch()}
        {renderNav(true, onMobileClose)}
        <div className="py-3 px-4" style={{ borderTop: '1px solid #363B41' }}>
          <div className="flex items-center justify-between">
            <span className="text-xs truncate" style={{ color: '#8A939C' }}>{user?.username}</span>
            <button
              onClick={logout}
              className="text-xs transition-colors shrink-0"
              style={{ color: '#8A939C' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#E8823A')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#8A939C')}
            >
              Salir
            </button>
          </div>
        </div>
      </aside>

      {/* Desktop: fijo con nombres (por defecto) o barra de íconos. Cambia el ancho de golpe, sin animar el layout. */}
      <aside
        className={`hidden md:flex flex-col shrink-0 ${fijo ? 'w-60' : 'w-14'}`}
        style={sidebarStyle}
      >
        {/* Header */}
        <div
          className={`py-4 flex shrink-0 ${fijo ? 'px-4 items-center justify-between gap-2' : 'flex-col items-center gap-2'}`}
          style={{ borderBottom: '1px solid #363B41' }}
        >
          <LogoArea expanded={fijo} />
          <BotonFijar />
        </div>

        {fijo && renderSearch()}

        {renderNav(fijo)}

        {/* Footer */}
        <div
          className={`py-3 shrink-0 ${fijo ? 'px-4' : ''}`}
          style={{ borderTop: '1px solid #363B41' }}
        >
          {fijo ? (
            <div className="flex items-center justify-between">
              <span className="text-xs truncate" style={{ color: '#8A939C' }}>{user?.username}</span>
              <button
                onClick={logout}
                className="text-xs transition-colors shrink-0"
                style={{ color: '#8A939C' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#E8823A')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#8A939C')}
              >
                Salir
              </button>
            </div>
          ) : (
            <button
              onClick={logout}
              title={`Salir (${user?.username ?? ''})`}
              aria-label="Salir"
              className="w-full flex justify-center py-1 rounded transition-colors"
              style={{ color: '#8A939C' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#E8823A')}
              onMouseLeave={(e) => (e.currentTarget.style.color = '#8A939C')}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          )}
        </div>
      </aside>
    </>
  );
}
